import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import Modal from 'react-modal';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import getCameraPlugin from '../utils/cameraPlugin';
import '../styles/seeContent.css';
import { compressImage, compressVideo } from '../utils/mediaCompressor';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, List, ListItem, ListItemText, TextField, CircularProgress, Autocomplete } from '@mui/material';

Modal.setAppElement('#root');

export default function SeeContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { photo, video } = location.state || {};

  const CameraPlugin = getCameraPlugin();

  const videoRef = useRef(null);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [savingToTrippin, setSavingToTrippin] = useState(false);
  const [flashing, setFlashing] = useState(false);
  const [savedToTrippin, setSavedToTrippin] = useState(false);
  const [flashed, setFlashed] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingUploadData, setPendingUploadData] = useState(null);

  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [closeFriends, setCloseFriends] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // Locality confirmation (when reverse geocode fails)
  const [placeDialogOpen, setPlaceDialogOpen] = useState(false);
  const [placeDialogBusy, setPlaceDialogBusy] = useState(false);
  const [placeFileName, setPlaceFileName] = useState(null);
  const [placeLat, setPlaceLat] = useState(null);
  const [placeLon, setPlaceLon] = useState(null);
  const [placeNameInput, setPlaceNameInput] = useState('');
  const [nearbySuggestions, setNearbySuggestions] = useState([]);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState(null);

  // Multiple place options dialog
  const [placeOptionsData, setPlaceOptionsData] = useState(null);

  // Reverse geocoding result
  const [reverseGeocodeResult, setReverseGeocodeResult] = useState(null);
  const [reverseGeocodeLoading, setReverseGeocodeLoading] = useState(false);
  const [reverseGeocodeError, setReverseGeocodeError] = useState(null);
  // Shared media key (R2 object key) to avoid uploading the same image twice across flows
  const [sharedMediaKey, setSharedMediaKey] = useState(null); // string | null

  // Helpers to normalize and format locality display without country
  const normalizeLocality = (rg) => {
    const loc = rg?.locality || {};
    const ctx = loc?.context || {};
    // Prefer explicit fields, then context fallbacks
    return {
      area: loc.area || ctx.area_name || '',
      village: loc.village || ctx.village_name || '',
      city: loc.city || ctx.city_name || '',
      state: loc.state || ctx.state_name || ''
    };
  };

  const formatLocalityList = (loc, max = 4) => {
    const parts = [(loc.area || '').trim(), (loc.village || '').trim(), (loc.city || '').trim(), (loc.state || '').trim()]
      .filter(Boolean)
      .slice(0, max);
    return parts.join(', ');
  };

  // On mount, get coordinates and call reverse geocoding
  useEffect(() => {
    console.log('[SeeContent] Reverse geocode useEffect triggered');
    let cancelled = false;
    const fetchReverseGeocode = async () => {
      console.log('[SeeContent] Starting reverse geocode fetch...');
      setReverseGeocodeLoading(true);
      setReverseGeocodeError(null);
      let lat = null, lon = null;
      // Try to get from photo metadata first
      if (photo && photo.latitude && photo.longitude) {
        lat = photo.latitude;
        lon = photo.longitude;
        console.log('[SeeContent] Using coordinates from photo metadata:', { lat, lon });
      } else {
        try {
          console.log('[SeeContent] Getting current position from device...');
          // Increase timeout and add options for better compatibility
          const geo = await Geolocation.getCurrentPosition({ 
            enableHighAccuracy: true,
            timeout: 15000, // 15 seconds timeout
            maximumAge: 0 // Don't use cached position
          });
        lat = geo.coords.latitude;
        lon = geo.coords.longitude;
          console.log('[SeeContent] Got current position:', { lat, lon });
        } catch (err) {
          console.error('[SeeContent] Failed to get location:', err);
          console.error('[SeeContent] Error code:', err.code, 'Message:', err.message);
          
          // Fallback: Use a default location (Cyprus) or show error
          // Code 1 = PERMISSION_DENIED, Code 2 = POSITION_UNAVAILABLE, Code 3 = TIMEOUT
          if (err.code === 1) {
            console.warn('[SeeContent] Location permission denied by user');
            setReverseGeocodeError('Location permission denied. Please enable location access.');
          } else if (err.code === 3) {
            console.warn('[SeeContent] Geolocation timeout - trying with lower accuracy...');
            // Retry with lower accuracy and longer timeout
            try {
              const fallbackGeo = await Geolocation.getCurrentPosition({
                enableHighAccuracy: false,
                timeout: 30000,
                maximumAge: 300000 // Accept 5 minute old position
              });
              lat = fallbackGeo.coords.latitude;
              lon = fallbackGeo.coords.longitude;
              console.log('[SeeContent] Got fallback position:', { lat, lon });
            } catch (fallbackErr) {
              console.error('[SeeContent] Fallback geolocation also failed:', fallbackErr);
              setReverseGeocodeError('Location unavailable. Check device settings.');
              setReverseGeocodeLoading(false);
              return;
            }
          } else {
            setReverseGeocodeError('Could not get location');
            setReverseGeocodeLoading(false);
            return;
          }
        }
      }
      setPlaceLat(lat);
      setPlaceLon(lon);
      console.log('[SeeContent] Coordinates set, calling reverse-geocode API...');
      try {
        const { data } = await axiosInstance.post('/reverse-geocode/initiate', { latitude: lat, longitude: lon });
        console.log('[SeeContent] Reverse geocode response:', data);
        console.log('[SeeContent] Response has place:', !!data?.place);
        console.log('[SeeContent] Response has locality:', !!data?.locality);
        console.log('[SeeContent] Locality data:', data?.locality);
        if (!cancelled) {
          setReverseGeocodeResult(data);
          // New backend returns single best result with alternatives - no dialog needed
        }
      } catch (err) {
        console.error('[SeeContent] Reverse geocode API call failed:', err);
        console.error('[SeeContent] Error details:', { 
          message: err.message, 
          status: err.response?.status, 
          data: err.response?.data 
        });
        if (!cancelled) setReverseGeocodeError('Reverse geocoding failed');
      } finally {
        if (!cancelled) setReverseGeocodeLoading(false);
      }
    };
    fetchReverseGeocode().catch(err => {
      console.error('[SeeContent] Unhandled error in reverse geocode:', err);
    });
    return () => { 
      console.log('[SeeContent] Reverse geocode useEffect cleanup');
      cancelled = true; 
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo]);

  // Place creation state
  const [editPlaceDialogOpen, setEditPlaceDialogOpen] = useState(false);
  const [userCreatedReverseGeocode, setUserCreatedReverseGeocode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editedLocality, setEditedLocality] = useState({
    area: '',
    village: '',
    city: '',
    state: '',
    country: '',
    locality1: '',
    locality2: '',
    locality3: ''
  });

  // Category autocomplete state
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const categorySearchTimer = useRef(null);
  const [showNoMatches, setShowNoMatches] = useState(false);
  const blurTimeoutRef = useRef(null);
  // Validation state: true when current input matches an existing category
  const [isCategoryValid, setIsCategoryValid] = useState(null); // null | boolean
  const [categoryTouched, setCategoryTouched] = useState(false);

  const searchCategories = (term) => {
    if (!term || !term.trim()) {
      setCategorySuggestions([]);
      setIsCategoryValid(null);
      return;
    }

    // Debounce: clear previous timer
    if (categorySearchTimer.current) clearTimeout(categorySearchTimer.current);

  // Reset indicators while typing
  setShowNoMatches(false);
  setCategoryTouched(true);

  categorySearchTimer.current = setTimeout(async () => {
      try {
        // Use axiosInstance (configured baseURL and auth/CSRF handling)
        const res = await axiosInstance.get('/categories/search', { params: { q: term.trim(), limit: 20 } });

        // axios normalizes; check data
        if (!res || res.status !== 200) {
          console.warn('Category search request non-200', res?.status);
          setCategorySuggestions([]);
          return;
        }

        const contentType = (res.headers?.['content-type'] || '').toLowerCase();
        if (contentType && contentType.includes('application/json')) {
          const items = res.data?.items || [];
          setCategorySuggestions(items);
          const typedLower = term.trim().toLowerCase();
          const isExact = items.some(it => (it.name || '').toLowerCase() === typedLower);
          setIsCategoryValid(isExact ? true : (items.length === 0 ? false : false));
          setShowNoMatches(items.length === 0);
        } else {
          // Unexpected content-type (HTML); log snippet if available
          console.warn('Category search returned non-JSON response (axios):', { status: res.status, snippet: String(res.data).slice(0,200) });
          setCategorySuggestions([]);
          setShowNoMatches(false);
          setIsCategoryValid(null);
        }
      } catch (e) {
        // axios throws for non-2xx; handle auth redirect/refresh flows via axiosInstance internals
        if (e.response) {
          if (e.response.status === 401 || e.response.status === 403) {
            console.warn('Category search unauthorized (status)', e.response.status);
            setCategorySuggestions([]);
            setShowNoMatches(false);
            setIsCategoryValid(null);
            return;
          }
          // Non-JSON body may still be present
          const bodySnippet = String(e.response.data).slice(0,200);
          console.warn('Category search failed with response:', e.response.status, bodySnippet);
        } else {
          console.warn('Category search network error:', e.message || e);
        }
        setCategorySuggestions([]);
        setShowNoMatches(false);
        setIsCategoryValid(null);
      }
    }, 250); // 250ms debounce
  };

  // Exact-match check for the typed category (used on Enter)
  const exactCheckCategory = async (term) => {
    if (!term || !term.trim()) {
      setShowNoMatches(false);
      setIsCategoryValid(null);
      return;
    }
    try {
      const res = await axiosInstance.get('/categories/search', { params: { q: term.trim(), limit: 50 } });
      if (res && res.status === 200) {
        const items = res.data?.items || [];
        const match = items.find(it => (it.name || '').toLowerCase() === term.trim().toLowerCase());
        if (match) {
          setSelectedCategory(match.name);
          setCategorySuggestions([]);
          setShowNoMatches(false);
          setIsCategoryValid(true);
          return true;
        }
      }
    } catch (e) {
      console.warn('Exact category check failed', e?.response?.status || e.message || e);
    }
    setShowNoMatches(true);
    setCategorySuggestions([]);
    setIsCategoryValid(false);
    return false;
  };

  // Derived inline validation: category typed but not selected and no matches
  const showCategoryInlineError = !!(categoryTouched && (selectedCategory || '').trim() && isCategoryValid === false);

  // Cleanup debounce timer on unmount to avoid leaking timeouts
  useEffect(() => {
    return () => {
      if (categorySearchTimer.current) {
        clearTimeout(categorySearchTimer.current);
        categorySearchTimer.current = null;
      }
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const forcePlay = async () => {
      if (videoRef.current && video) {
        try {
          videoRef.current.pause();
          videoRef.current.load();

          await new Promise((resolve) => setTimeout(resolve, 100));

          await videoRef.current.play();
          console.log("✅ Video playback started");
        } catch (err) {
          console.warn("⚠️ Video play failed:", err);
        }
      }
    };

    forcePlay();
  }, [video]);

  useEffect(() => {
    const fetchCloseUsers = async () => {
      try {
        console.log('[SeeContent] Fetching close users...');
        const response = await axiosInstance.get('/flashes/close-users');
        setCloseFriends(response.data.users || []);
        console.log('[SeeContent] Close users fetched successfully:', response.data.users?.length || 0);
      } catch (err) {
        console.warn('[SeeContent] Error fetching close users (non-critical):', err.message);
        // Set empty array on error - this is non-critical
        setCloseFriends([]);
      }
    };

    // Run independently without blocking other operations
    fetchCloseUsers().catch(err => console.warn('[SeeContent] Unhandled close-users error:', err));
  }, []);

  const handleDiscard = async () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }

    if (video?.startsWith('blob:')) {
      URL.revokeObjectURL(video);
    }

    if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      navigate(-1);

      const result = await CameraPlugin.openCamera({ animated: false });

      if (!result || !result.fileUri) {
        console.error('Error: No fileUri received from android or iOS.');
        return;
      }

      if (result.isVideo) {
        navigate('/see-content', { state: { video: result.fileUri } });
      }
      else {
        navigate('/see-content', { state: { photo: result.fileUri } });
      }
    }
    else {
      navigate(-1);
    }
  };

  const handleDownload = async () => {
    if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      try {
        const isVideo = !!video;
        let filePath = isVideo ? video : photo;

        if (filePath.startsWith("data:video/mp4;base64,")) {
          console.log("📂 Converting Base64 video to file...");

          const base64Data = filePath.split(",")[1];

          const savedFile = await Filesystem.writeFile({
            path: "saved_video.mp4",
            data: base64Data,
            directory: Directory.Documents,
            recursive: true,
          });

          filePath = savedFile.uri;

          console.log("✅ Base64 video saved to:", filePath);
        }

        console.log("📂 Final File Path Sent to Native:", filePath);

        await CameraPlugin.saveToGallery({
          filePath,
          isVideo,
          stripAudio: !isAudioOn,
        });
      } catch (error) {
        console.error(error);
      }
    }
    else {
      try {
        const link = document.createElement('a');

        if (photo) {
          link.href = photo;
          link.download = 'captured_photo.png';
        }
        else if (video) {
          if (isAudioOn) {
            const response = await fetch(video);
            if (!response.ok) {
              throw new Error('Failed to fetch video for download');
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            link.href = blobUrl;
            link.download = 'captured_video.mp4';

            link.addEventListener('click', () => {
              setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
            });
          }
          else {
            const videoResponse = await fetch(video);
            if (!videoResponse.ok) {
              throw new Error('Failed to fetch video for stripping audio');
            }

            const videoBlob = await videoResponse.blob();
            const formData = new FormData();
            formData.append('file', videoBlob, 'video.mp4');

            const response = await axiosInstance.post('/strip-audio', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              responseType: 'blob',
            });

            const processedBlob = response.data;
            const blobUrl = URL.createObjectURL(processedBlob);

            link.href = blobUrl;
            link.download = 'captured_video_no_audio.mp4';

            link.addEventListener('click', () => {
              setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
            });
          }
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error during download:', error);
        alert('An error occurred while downloading the file.');
      }
    }
  };

  const toggleAudio = () => {
    setIsAudioOn(prev => !prev);
  };

  const handleSaveToTrippin = async () => {
    setSavingToTrippin(true);
    try {
      const isVideo = !!video;
      // Compress on commit, but keep UI preview showing the original
      let compressed;
      if (isVideo) {
        console.log('[SeeContent] compressVideo:start', { uriSnippet: String(video).slice(0, 80) });
        compressed = await compressVideo(video, { maxSize: 1080, bitrate: 2_000_000, keepAudio: isAudioOn });
      } else {
        console.log('[SeeContent] compressImage:start', { uriSnippet: String(photo).slice(0, 80) });
        compressed = await compressImage(photo, { maxWidth: 1920, maxHeight: 1920, quality: 0.8, format: 'JPEG' });
      }
      console.log('[SeeContent] compress:result', { uriSnippet: String(compressed?.uri || '').slice(0, 80), width: compressed?.width, height: compressed?.height, size: compressed?.size });
      const fileBlob = await fetch(compressed.uri).then((res) => res.blob());
      console.log('[SeeContent] blob:ready', { type: fileBlob.type, size: fileBlob.size });

    // Include the blob's MIME type so backend signs URL with matching contentType.
    const res = await axiosInstance.post('/trippin/generate-upload-url', {
      isVideo,
      contentType: fileBlob.type || (isVideo ? 'video/mp4' : 'image/jpeg'),
      // Reuse previously uploaded R2 key for the current media (image or video)
      existingKey: sharedMediaKey || undefined,
    });
    console.log('[SeeContent] signed-url:response', { status: res?.status, fileName: res?.data?.fileName, uploadUrlSnippet: String(res?.data?.uploadUrl || '').slice(0, 100), contentType: fileBlob.type });
    
    // Use cached coordinates from reverse geocoding
    let latitude = placeLat;
    let longitude = placeLon;
    
    // Fallback to getting current position if not available
    if (!latitude || !longitude) {
      try {
        const coordinates = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
        latitude = coordinates.coords.latitude;
        longitude = coordinates.coords.longitude;
      } catch (err) {
        // Hard-coded Cyprus coordinates as ultimate fallback
        longitude = '33.120326';  // This is actually longitude (East)
        latitude = '35.176092'; 
      }
    }
    
    const askToStartTrippin = res.data.askToStartTrippin;
    if (askToStartTrippin) {
      setPendingUploadData({
        latitude,
        longitude,
        isVideo,
        stripAudio: !isAudioOn ? 'true' : undefined,
      });
      console.log('hello');
      setIsModalOpen(true);
      return;
    }
  const { uploadUrl, fileName } = res.data;

    if (uploadUrl) {
      const uploadResp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 
          'Content-Type': fileBlob.type,
        },
        body: fileBlob,
      });
      console.log('[SeeContent] upload:put:done', { status: uploadResp.status, ok: uploadResp.ok });
      if (!uploadResp.ok) {
        const errText = await uploadResp.text().catch(() => '');
        console.error('[SeeContent] storage upload failed', uploadResp.status, errText);
        alert('Upload to storage failed. Please retry.');
        setSavingToTrippin(false);
        return;
      }
    } else {
      console.log('[SeeContent] reuse existing R2 key, skipping upload');
    }

  // Cache the key so flashing can reuse it (images and videos)
  if (fileName) setSharedMediaKey(fileName);
    
    try {
      // Use simplified save-metadata with cached reverse geocoding data
      const payload = {
        fileName,
        latitude,
        longitude,
        stripAudio: !isAudioOn ? 'true' : undefined,
        cachedReverseGeocode: reverseGeocodeResult // Pass cached data to backend
      };
      console.log('[SeeContent] save-metadata:request', payload);
      const response = await axiosInstance.post('/trippin/save-metadata', payload);
      console.log('[SeeContent] save-metadata:response', { status: response?.status, data: response?.data });
      
      // If server returned lockedSelection, update local reverseGeocodeResult so UI locks
      if (response.data?.lockedSelection) {
        setReverseGeocodeResult({
          place: response.data.lockedSelection.place,
          locality: response.data.lockedSelection.locality,
          alternatives: []
        });
      }

      // Simple success response - no complex confirmation logic needed
      setSavingToTrippin(false);
      setSavedToTrippin(true); // Disable button after success
      // Ensure UI no longer shows the "Change" option for a saved selection
      setReverseGeocodeResult(prev => prev ? ({ ...prev, alternatives: [] }) : prev);
  // Remove create button since user saved with current selection
  setUserCreatedReverseGeocode(true);
      
    } catch (e) {
      setSavingToTrippin(false);
      console.error('[SeeContent] save-metadata error', { status: e?.response?.status, data: e?.response?.data });
      
      // Show place creation dialog if no place was found
      if (e?.response?.status === 400 || !reverseGeocodeResult?.place) {
        setPlaceFileName(fileName);
        setPlaceLat(latitude);
        setPlaceLon(longitude);
        
        // Populate the form with all available data
        populateCreatePlaceForm();
        
        setEditPlaceDialogOpen(true);
      } else {
        alert('An error occurred while saving to trippin.');
      }
    }
    
  } catch (err) {
    console.error('Error while saving image to Trippin:', err?.response?.status, err?.response?.data || err);
    alert('An error occurred while saving the image.');
  } finally {
    setSavingToTrippin(false);
  }
};

  
  const confirmStartTrippin = async () => {
    try {
      if (pendingUploadData) {
        const { latitude, longitude, isVideo, stripAudio } = pendingUploadData;
        // compress again deterministically (will hit cache and skip work)
        let compressed;
        if (isVideo) {
          console.log('[SeeContent] confirmStartTrippin compressVideo:start');
          compressed = await compressVideo(video, { maxSize: 1080, bitrate: 2_000_000, keepAudio: isAudioOn });
        } else {
          console.log('[SeeContent] confirmStartTrippin compressImage:start');
          compressed = await compressImage(photo, { maxWidth: 1920, maxHeight: 1920, quality: 0.8, format: 'JPEG' });
        }
        console.log('[SeeContent] confirmStartTrippin compress:result', { uriSnippet: String(compressed?.uri || '').slice(0, 80), size: compressed?.size });
        const fileBlob = await fetch(compressed.uri).then((res) => res.blob());
        console.log('[SeeContent] confirmStartTrippin blob:ready', { type: fileBlob.type, size: fileBlob.size });

      const res = await axiosInstance.post('/trippin/generate-upload-url', {
        isVideo,
        contentType: fileBlob.type || (isVideo ? 'video/mp4' : 'image/jpeg'),
        existingKey: sharedMediaKey || undefined,
      }, {
        headers: {
          'Start-Trippin': 'true'
        }
      });
      const { uploadUrl, fileName } = res.data;
      console.log('[SeeContent] confirmStartTrippin signed-url', { fileName, uploadUrlSnippet: String(uploadUrl || '').slice(0, 100) });
    if (uploadUrl) {
        const uploadResp = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 
            'Content-Type': fileBlob.type,
          },
          body: fileBlob,
        });
        console.log('[SeeContent] confirmStartTrippin upload:put:done', { status: uploadResp.status, ok: uploadResp.ok });
        if (!uploadResp.ok) {
          const errText = await uploadResp.text().catch(() => '');
          console.error('[SeeContent] storage upload (start trippin) failed', uploadResp.status, errText);
          alert('Upload to storage failed. Please retry.');
          return;
        }
      } else {
        console.log('[SeeContent] confirmStartTrippin reuse existing R2 key, skipping upload');
      }

  if (fileName) setSharedMediaKey(fileName);
     const response = await axiosInstance.post('/trippin/save-metadata', {
        fileName,
        latitude,
        longitude,
        stripAudio,
        // Ensure backend can resolve labels/country when starting a new trippin
        cachedReverseGeocode: reverseGeocodeResult || undefined,
      }, {
        headers: {
          'Start-Trippin': 'true'
        }
      });
      console.log('[SeeContent] confirmStartTrippin save-metadata:response', { status: response?.status, data: response?.data });
  
        if (response.status === 200) {
          alert('Image saved and Trippin started!');
        }
        else {
          alert('Failed to start Trippin.');
        }
      }
    } catch (err) {
      console.error('Error while starting Trippin:', err);
      alert('An error occurred.');
    } finally {
      setIsModalOpen(false);
      setPendingUploadData(null);
    }
  };

  const cancelStartTrippin = () => {
    setIsModalOpen(false);
    setPendingUploadData(null);
  };

  const closePlaceDialog = () => {
    if (placeDialogBusy) return;
    setPlaceDialogOpen(false);
    setPlaceDialogBusy(false);
    setPlaceFileName(null);
    setPlaceLat(null);
    setPlaceLon(null);
    setPlaceNameInput('');
    setSelectedSuggestionId(null);
    setNearbySuggestions([]);
    setPlaceOptionsData(null);
  };

  // Helper function to populate the create place form with all available data
  const populateCreatePlaceForm = (geocodeResult = null) => {
    const result = geocodeResult || reverseGeocodeResult;
    if (!result) return;
    
  const locality = result?.locality || {};
  const context = locality.context || {};
    const place = result?.place || {};
    
    console.log('[SeeContent] Populating form with:', { locality, context, place });
    
    // Populate place name and category if available
    if (place.name) {
      setPlaceNameInput(place.name);
    }
    if (place.category) {
      setSelectedCategory(place.category);
    }
    
    // Populate locality hierarchy - check all available sources
    setEditedLocality({
      // Area - check multiple sources
  area: locality.area ||
    context.area_name ||
    (locality.type === 'area' ? locality.name : '') ||
    place.locality?.area || '',
            
      // Village/Area - check multiple sources
  village: locality.village ||
       context.village_name ||
       (locality.type === 'village' ? locality.name : '') ||
       place.locality?.village || '',
               
  // City - check multiple sources (do NOT fall back to locality.name)
  city: locality.city ||
    context.city_name ||
    (locality.type === 'city' ? locality.name : '') ||
    place.locality?.city || '',
            
      // State - check multiple sources  
      state: locality.state ||
             context.state_name || 
             (locality.type === 'state' ? locality.name : '') ||
             place.locality?.state || '',
             
      // Country - default to Cyprus if not specified
      country: locality.country ||
               context.country_name || 
               (locality.type === 'country' ? locality.name : '') ||
               place.locality?.country || 
               'Cyprus'
    });
  };

  const closeEditPlaceDialog = () => {
    setEditPlaceDialogOpen(false);
    setSelectedCategory('');
    setEditedLocality({
      area: '',
      village: '',
      city: '',
      state: '',
      country: '',
      locality1: '',
      locality2: '',
      locality3: ''
    });
  };

  // Info overlay (centered) for place/locality details with labels
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoData, setInfoData] = useState(null); // { place?: {name,category}, locality: {area,village,city,state} }

  const openInfo = (data) => {
    setInfoData(data);
    setInfoOpen(true);
  };
  const closeInfo = () => {
    setInfoOpen(false);
    setInfoData(null);
  };

  // Check if current form input matches exactly what was prefilled from cache
  const isInputUnchangedFromCache = () => {
    if (!reverseGeocodeResult) return false;
    
    const locality = reverseGeocodeResult?.locality || {};
    const context = locality.context || {};
    const place = reverseGeocodeResult?.place || {};
    
    // Get original prefilled values
    const originalLocality = {
  area: locality.area ||
    context.area_name ||
    (locality.type === 'area' ? locality.name : '') ||
    place.locality?.area || '',
  village: locality.village ||
       context.village_name ||
       (locality.type === 'village' ? locality.name : '') ||
       place.locality?.village || '',
  city: locality.city || 
    context.city_name || 
    (locality.type === 'city' ? locality.name : '') ||
    place.locality?.city || '',
  state: locality.state ||
     context.state_name || 
     (locality.type === 'state' ? locality.name : '') ||
     place.locality?.state || '',
  country: locality.country ||
       context.country_name || 
       (locality.type === 'country' ? locality.name : '') ||
       place.locality?.country || 
       'Cyprus'
    };
    
    const originalPlaceName = '';
    const originalCategory = '';
    
    // Compare current form values with original prefilled values (with null checks)
    const currentPlaceName = (placeNameInput || '').trim();
    const currentCategory = (selectedCategory || '').trim();
    
    const localityMatches = (
      (editedLocality?.area || '').trim() === originalLocality.area &&
      (editedLocality?.village || '').trim() === originalLocality.village &&
      (editedLocality?.city || '').trim() === originalLocality.city &&
      (editedLocality?.state || '').trim() === originalLocality.state &&
      (editedLocality?.country || '').trim() === originalLocality.country
    );
    
    const placeMatches = (
      currentPlaceName === originalPlaceName &&
      currentCategory === originalCategory
    );
    
    return localityMatches && placeMatches;
  };

  // Check if current input matches any existing suggestion from cache
  const isInputMatchingExistingSuggestion = () => {
    if (!reverseGeocodeResult?.place) return false;
    
    const currentPlaceName = (placeNameInput || '').trim().toLowerCase();
    const currentCategory = (selectedCategory || '').trim().toLowerCase();
    
    // Check if matches the main suggested place (including locality)
    const mainPlace = reverseGeocodeResult.place;
    const mainLocality = reverseGeocodeResult.locality || {};
    if (mainPlace && 
        (mainPlace.name || '').toLowerCase() === currentPlaceName &&
        (mainPlace.category || '').toLowerCase() === currentCategory) {
      // Also require locality to match; if user changed locality, consider it not matching
      const locMatches = (
        ((editedLocality?.area || '').toLowerCase() || '') === ((mainLocality?.area || '').toLowerCase() || '') &&
        ((editedLocality?.village || '').toLowerCase() || '') === ((mainLocality?.village || '').toLowerCase() || '') &&
        ((editedLocality?.city || '').toLowerCase() || '') === ((mainLocality?.city || '').toLowerCase() || '') &&
        ((editedLocality?.state || '').toLowerCase() || '') === ((mainLocality?.state || '').toLowerCase() || '') &&
        ((editedLocality?.country || '').toLowerCase() || '') === ((mainLocality?.country || '').toLowerCase() || '')
      );
      if (locMatches) return true;
    }
    
    // Check if matches any of the alternative options
    if (placeOptionsData?.options?.length > 0) {
      return placeOptionsData.options.some(opt => {
        const normalizedInput = {
          name: currentPlaceName,
          category: currentCategory || 'user_generated',
          locality: {
            area: (editedLocality?.area || '').toLowerCase(),
            village: (editedLocality?.village || '').toLowerCase(),
            city: (editedLocality?.city || '').toLowerCase(),
            state: (editedLocality?.state || '').toLowerCase(),
            country: (editedLocality?.country || '').toLowerCase()
          }
        };
        
        return (
          (opt.name || '').toLowerCase() === normalizedInput.name &&
          (opt.category || '').toLowerCase() === normalizedInput.category &&
          ((opt.locality?.area || '').toLowerCase() === normalizedInput.locality.area) &&
          ((opt.locality?.village || '').toLowerCase() === normalizedInput.locality.village) &&
          ((opt.locality?.city || '').toLowerCase() === normalizedInput.locality.city) &&
          ((opt.locality?.state || '').toLowerCase() === normalizedInput.locality.state) &&
          ((opt.locality?.country || '').toLowerCase() === normalizedInput.locality.country)
        );
      });
    }
    
    return false;
  };

  const createNewPlace = async () => {
    // Allow creation even without place name if user is just fixing localities
    const placeName = (placeNameInput || '').trim();
    const category = (selectedCategory || '').trim();
    
    // Check if input is unchanged from prefilled cache values
    if (isInputUnchangedFromCache()) {
      alert('Please modify at least one field or select an existing place suggestion.');
      return;
    }
    
    // If a category string was provided, ensure it exists in our categories table
    if (category) {
      const categoryExists = await exactCheckCategory(category);
      if (!categoryExists) {
        alert('Please select a category from the provided suggestions.');
        return;
      }
    }
    
    // Validate that both name and category are provided if creating a place
    if ((placeName && !category) || (!placeName && category)) {
      alert('Both place name and category are required when creating a new place.');
      return;
    }
    
    // Note: We intentionally allow creating even if an identical suggestion exists.
    // The backend will dedupe or return the existing place where appropriate.

    setPlaceDialogBusy(true);
    try {
      const response = await axiosInstance.post('/places/create', {
        name: placeName || null, // Allow null name for locality-only creation
        category: category || 'user_generated',
        latitude: placeLat,
        longitude: placeLon,
        locality: {
          area: editedLocality.area || '',
          village: editedLocality.village || '',
          city: editedLocality.city || '',
          state: editedLocality.state || '',
          country: editedLocality.country || 'Cyprus'
        }
      });

      const newPlaceId = response.data.placeId;
      
      // Update reverse geocode result with the new place
      if (response.data.place) {
        setReverseGeocodeResult({
          place: response.data.place,
          locality: response.data.locality,
          fromCache: false
        });
        // Mark that user created a place/locality so we remove the Create button
        setUserCreatedReverseGeocode(true);
        // Refresh server-side reverse geocode to pick up locked selection/cache
        try { await refetchReverseGeocodeFromServer(); } catch (e) { /* ignore */ }
      } else if (response.data.success && !response.data.place && response.data.locality) {
        // Locality-only creation path: update state so UI reflects new locality
        setReverseGeocodeResult({
          locality: {
            name: response.data.locality.city || response.data.locality.village || response.data.locality.area || response.data.locality.state || response.data.locality.country || 'Locality',
            type: (response.data.locality.area && 'area') || (response.data.locality.village && 'village') || (response.data.locality.city && 'city') || (response.data.locality.state && 'state') || 'country',
            context: {
              city_name: response.data.locality.city || null,
              state_name: response.data.locality.state || null,
              country_name: response.data.locality.country || null,
              village_name: response.data.locality.village || null,
              area_name: response.data.locality.area || null
            }
          },
          fromCache: false
        });
        setUserCreatedReverseGeocode(true);
        try { await refetchReverseGeocodeFromServer(); } catch (e) { /* ignore */ }
      }

      // Continue with saving content using new place
      if (placeOptionsData) {
        if (placeOptionsData.type === 'flash') {
          await axiosInstance.post('/flash/save-metadata', {
            fileName: placeOptionsData.fileName,
            latitude: placeOptionsData.latitude,
            longitude: placeOptionsData.longitude,
            isCloseFriends: placeOptionsData.isCloseFriends,
            tags: placeOptionsData.tags,
            stripAudio: placeOptionsData.stripAudio,
            selectedId: newPlaceId
          });
          alert('Flash saved with new place!');
        } else if (placeOptionsData.type === 'trippin') {
          await axiosInstance.post('/trippin/save-metadata', {
            fileName: placeOptionsData.fileName,
            latitude: placeOptionsData.latitude,
            longitude: placeOptionsData.longitude,
            stripAudio: placeOptionsData.stripAudio,
            selectedId: newPlaceId
          });
          alert('Trippin saved with new place!');
        }
      } else {
        // Handle the original place creation flow
        if (placeName) {
          await axiosInstance.post('/trippin/with-custom-place', {
            placeName: placeName,
            category: category || 'user_generated',
            latitude: placeLat,
            longitude: placeLon,
            contentUrl: placeFileName,
            locality: editedLocality
          });
          alert('Saved to trippin with new place');
        } else {
          alert('Locality information updated! You can now save your content.');
        }
      }

  // Close the create dialog (editPlaceDialog) and the place options dialog
  try { setEditPlaceDialogOpen(false); } catch (e) { /* ignore */ }
  closePlaceDialog();
    } catch (err) {
      console.error('Error creating new place:', err);
      if (err?.response?.status === 400 && err.response.data?.error === 'invalid_category') {
        alert('Please select a valid category from the suggestions.');
        setShowNoMatches(true);
        setCategorySuggestions([]);
        return;
      }
      alert('Failed to create new place. Please try again.');
    } finally {
      setPlaceDialogBusy(false);
    }
  };

  // Helper to refetch server-side reverse geocode (to pick up locked selection / updated cache)
  const refetchReverseGeocodeFromServer = async () => {
    try {
      if (placeLat == null || placeLon == null) return;
      const { data } = await axiosInstance.post('/reverse-geocode/initiate', { latitude: placeLat, longitude: placeLon });
      if (data) {
        setReverseGeocodeResult(data);
      }
    } catch (e) {
      console.warn('Refetch reverse geocode failed', e);
    }
  };
  // Handle selection of a place option when multiple options are available
  const handlePlaceOptionSelection = async (selectedOptionId) => {
    if (!placeOptionsData) return;
    
    setPlaceDialogBusy(true);
    try {
      if (placeOptionsData.type === 'flash') {
        // Re-submit flash with selected option
        await axiosInstance.post('/flash/save-metadata', {
          fileName: placeOptionsData.fileName,
          latitude: placeOptionsData.latitude,
          longitude: placeOptionsData.longitude,
          isCloseFriends: placeOptionsData.isCloseFriends,
          tags: placeOptionsData.tags,
          stripAudio: placeOptionsData.stripAudio,
          selectedId: selectedOptionId
        });
        alert('Flash saved successfully!');
      } else if (placeOptionsData.type === 'trippin') {
        // Re-submit trippin with selected option
        await axiosInstance.post('/trippin/save-metadata', {
          fileName: placeOptionsData.fileName,
          latitude: placeOptionsData.latitude,
          longitude: placeOptionsData.longitude,
          stripAudio: placeOptionsData.stripAudio,
          selectedId: selectedOptionId
        });
        alert('Trippin saved successfully!');
      } else if (placeOptionsData.type === 'initial_selection') {
        // Initial place selection from reverse geocoding - store selection for later use
        const selectedPlace = placeOptionsData.options.find(opt => opt.id === selectedOptionId);
        if (selectedPlace) {
          setReverseGeocodeResult({
            place: selectedPlace,
            locality: selectedPlace.locality || reverseGeocodeResult?.locality || null,
            fromCache: placeOptionsData.fromCache
          });
          console.log('Selected place for future operations:', selectedPlace);
        }
  // alert removed: selection is silent and UI reflects updated state
  // Selection stored silently; UI will update to reflect single selection without alerts
  // alert('Place selected! You can now proceed with creating your flash or trippin.');
  // alert('Place selected! You can now proceed with creating your flash or trippin.');
      }
      closePlaceDialog();
    } catch (err) {
      console.error('Error submitting with selected place:', err);
      alert('Failed to save with selected place.');
    } finally {
      setPlaceDialogBusy(false);
    }
  };

  const submitPlaceDialog = async () => {
    if (!placeFileName || placeLat == null || placeLon == null) return;
    if (!placeNameInput?.trim()) {
      alert('Please enter a place name');
      return;
    }
    
    setPlaceDialogBusy(true);
    
    try {
      // Check if this is an OSM candidate that needs confirmation
      if (selectedSuggestionId && selectedSuggestionId.startsWith('candidate_')) {
        const candidateId = selectedSuggestionId.replace('candidate_', '');
        
        // Confirm the OSM-derived place
        await axiosInstance.post('/trippin/confirm-place', {
          candidateId: parseInt(candidateId),
          confirmedName: placeNameInput?.trim(),
          confirmedCategory: nearbySuggestions.find(s => s.id === selectedSuggestionId)?.category
        });
        
        // Then save with the confirmed place
        await axiosInstance.post('/trippin/save-metadata', {
          fileName: placeFileName,
          latitude: placeLat,
          longitude: placeLon,
          selectedId: `pending_${candidateId}` // Reference the pending place
        });
      } else if (selectedSuggestionId) {
        // Regular place selection
        await axiosInstance.post('/trippin/save-metadata', {
          fileName: placeFileName,
          latitude: placeLat,
          longitude: placeLon,
          name: placeNameInput?.trim() || undefined,
          selectedId: selectedSuggestionId || undefined,
        });
      } else {
        // No suggestion selected - create custom place
        await axiosInstance.post('/trippin/with-custom-place', {
          placeName: (placeNameInput || '').trim(),
          category: 'other', // Default category for custom places
          latitude: placeLat,
          longitude: placeLon,
          contentUrl: placeFileName
        });
      }
      
      closePlaceDialog();
  // Mark created so UI updates (remove create button)
  setUserCreatedReverseGeocode(true);
  // Refresh server-side reverse geocode to pick up locked selection/cache
  try { await refetchReverseGeocodeFromServer(); } catch (e) { /* ignore */ }
      alert('Saved to trippin');
    } catch (e) {
      console.error('place confirm failed', e);
      if (e?.response?.status === 404) {
        alert('Place not found. Please select a different option.');
      } else {
        alert('Could not save. Please try again.');
      }
      setPlaceDialogBusy(false);
    }
  };

const handleFlash = async (isCloseFriends) => {
  setFlashing(true);
  try {
    const isVideo = !!video;
    let compressed;
    if (isVideo) {
      console.log('[SeeContent] flash compressVideo:start');
      compressed = await compressVideo(video, { maxSize: 1080, bitrate: 2_000_000, keepAudio: isAudioOn });
    } else {
      console.log('[SeeContent] flash compressImage:start');
      compressed = await compressImage(photo, { maxWidth: 1920, maxHeight: 1920, quality: 0.8, format: 'JPEG' });
    }
    console.log('[SeeContent] flash compress:result', { uriSnippet: String(compressed?.uri || '').slice(0, 80), size: compressed?.size });
    const fileBlob = await fetch(compressed.uri).then((res) => res.blob());
    console.log('[SeeContent] flash blob:ready', { type: fileBlob.type, size: fileBlob.size });

    // 1. Get signed upload URL
    const { uploadUrl, fileName } = await axiosInstance.post('/flash/generate-upload-url', {
      isVideo,
      contentType: fileBlob.type || (isVideo ? 'video/mp4' : 'image/jpeg'),
      existingKey: sharedMediaKey || undefined,
    }).then(res => res.data);
    console.log('[SeeContent] flash signed-url', { fileName, uploadUrlSnippet: String(uploadUrl || '').slice(0, 100) });

    // 2. Upload only if a new URL was generated; otherwise reuse prior upload
    if (uploadUrl) {
      const flashUploadResp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 
          'Content-Type': fileBlob.type,
        },
        body: fileBlob,
      });
      console.log('[SeeContent] flash upload:put:done', { status: flashUploadResp.status, ok: flashUploadResp.ok });
      if (!flashUploadResp.ok) {
        const errText = await flashUploadResp.text().catch(() => '');
        console.error('[SeeContent] Flash upload failed', flashUploadResp.status, errText);
        alert('Failed to upload flash media. Please retry.');
        setFlashing(false);
        return;
      }
    } else {
      console.log('[SeeContent] flash reuse existing R2 key, skipping upload');
    }

  if (fileName) setSharedMediaKey(fileName);

    // 3. Use cached coordinates from reverse geocoding
    let latitude = placeLat;
    let longitude = placeLon;
    
    // Fallback to getting current position if not available
    if (!latitude || !longitude) {
      try {
        const coordinates = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
        latitude = coordinates.coords.latitude;
        longitude = coordinates.coords.longitude;
      } catch (err) {
        // Hard-coded Cyprus coordinates as ultimate fallback
        longitude = '33.356650';
        latitude = '35.167094';
      }
    }

    // 4. Check if we already have cached reverse geocoding data
    let response;
    if (reverseGeocodeResult && (reverseGeocodeResult.place || reverseGeocodeResult.locality)) {
      // Use cached reverse geocoding data
      const payload = {
        fileName,
        latitude,
        longitude,
        isCloseFriends,
        tags: selectedUsers,
        stripAudio: !isAudioOn ? 'true' : undefined,
        cachedReverseGeocode: reverseGeocodeResult // Pass cached data to backend
      };
      console.log('[SeeContent] flash save-metadata:request (with cache)', payload);
      response = await axiosInstance.post('/flash/save-metadata', payload);
    } else {
      // No cached data, let backend do reverse geocoding
      const payload = {
        fileName,
        latitude,
        longitude,
        isCloseFriends,
        tags: selectedUsers,
        stripAudio: !isAudioOn ? 'true' : undefined,
      };
      console.log('[SeeContent] flash save-metadata:request', payload);
      response = await axiosInstance.post('/flash/save-metadata', payload);
    }
    console.log('[SeeContent] flash save-metadata:response', { status: response?.status, data: response?.data });

    if (response.status === 200) {
      alert(response.data.message || 'Flash saved successfully!');
      setFlashed(true); // Disable button after success
      // Remove create button when user flashed
      setUserCreatedReverseGeocode(true);
    } else {
      alert('Failed to save Flash.');
    }
  } catch (err) {
    console.error('Error while flashing:', err);
    alert('An error occurred while saving the Flash.');
  } finally {
    setFlashing(false);
  }
};


  const handleFlashIt = () => setIsTagDialogOpen(true);

  const handleSearch = async (term) => {
    if (!term) {
      setFilteredUsers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get('/flashes/search-users', {
        params: { query: term },
      });
      setFilteredUsers(response.data.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const confirmTags = (isCloseFriends) => {
    setIsTagDialogOpen(false);
    handleFlash(isCloseFriends);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const displayedUsers = searchTerm ? filteredUsers : closeFriends;

  // Reusable dark TextField styles to match Tag users dialog
  const darkTextFieldSx = {
    // Base text color
    color: '#f5f5f5',
    // Apply dark background only to input container, not the whole field (so helper text area stays transparent)
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#1f1f1f',
      borderRadius: '10px',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#48c9b0' },
    },
    '& .MuiInputBase-root': {
      color: '#f5f5f5',
      fontSize: '14px',
      padding: '4px 10px',
    },
    '& .MuiInputBase-input': {
      padding: '8px 4px',
    },
    // Label colors
    '& .MuiFormLabel-root': { color: '#cbd5e1' },
    '& .MuiFormLabel-root.Mui-focused': { color: '#48c9b0' },
    // Helper text
    '& .MuiFormHelperText-root': { color: '#cbd5e1', marginLeft: 0, marginRight: 0 },
    // Placeholder
    '& .MuiInputBase-input::placeholder': { color: '#d1d5db', opacity: 1 },
    // Disabled styles (e.g., Country field)
    '& .MuiInputBase-root.Mui-disabled': {
      color: '#333',
      WebkitTextFillColor: '#333',
    },
  };

  return (
    <div className="tailwind see-content-container flex flex-col min-h-screen bg-black text-white">
      {(photo || video) ? (
        <>
          <div className="top-right-icons">
            <div className="icon-wrapper" onClick={handleDiscard}>
              <img
                src='assets/icons/discard.png'
                alt="Discard"
                className="icon-button"
              />

            </div>
            {/* Top action icons remain unchanged */}
            <div className="icon-wrapper" onClick={handleDownload}>
              <img
                src='assets/icons/download.png'
                alt="Download"
                className="icon-button"
              />
            </div>
            {video && (
              <div className="icon-wrapper" onClick={toggleAudio}>
                <img
                  src={isAudioOn ? 'assets/icons/audio_on.png' : 'assets/icons/audio_off.png'}
                  alt="Audio"
                  className="icon-button"
                />
              </div>
            )}
          </div>
          {/* Center media vertically (flex-1 ensures it grows; footer now in normal flow so no extra bottom padding needed) */}
          <div className="flex flex-1 w-full items-center justify-center pt-12">{/* footer inline below */}
            {photo ? (
              <img src={photo} alt="Captured" className="max-h-[70vh] object-contain" />
            ) : (
              <video
                ref={videoRef}
                loop
                playsInline
                autoPlay
                muted={!isAudioOn}
                className="max-h-[70vh] w-full object-contain"
              >
                <source src={video} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
          {/* Unified row: place details + circular buttons */}
          {reverseGeocodeLoading ? (
            // Skeleton / shimmer while loading reverse geocode
            <div className="mx-4 mb-4 rounded-xl bg-black/60 backdrop-blur-sm p-3 flex flex-col gap-2">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                <div className="shimmer" style={{ width: 80, height: 18, borderRadius: 999, background: 'linear-gradient(90deg,#e2e8f0 25%,#cbd5e1 50%,#e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmerAnimation 1.6s linear infinite' }} />
                <div className="shimmer" style={{ width: 80, height: 18, borderRadius: 999, background: 'linear-gradient(90deg,#e2e8f0 25%,#cbd5e1 50%,#e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmerAnimation 1.6s linear infinite' }} />
                <div className="shimmer" style={{ width: 100, height: 18, borderRadius: 999, background: 'linear-gradient(90deg,#e2e8f0 25%,#cbd5e1 50%,#e2e8f0 75%)', backgroundSize: '200% 100%', animation: 'shimmerAnimation 1.6s linear infinite' }} />
              </div>
            </div>
          ) : (
            reverseGeocodeResult && (
            <div
              className="w-full px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+44px)] flex items-start gap-2 cursor-pointer"
              onClick={() => {
                const baseLoc = reverseGeocodeResult.locality || {};
                const all = [reverseGeocodeResult.place, ...(reverseGeocodeResult.alternatives || [])].filter(Boolean).map(o => ({ ...o, locality: o.locality || baseLoc }));
                setPlaceOptionsData({ type: 'initial_selection', options: all, fromCache: reverseGeocodeResult.fromCache });
                // Preselect current place id if available
                if (reverseGeocodeResult.place?.id) setSelectedSuggestionId(reverseGeocodeResult.place.id);
                setPlaceDialogOpen(true);
              }}
            >
              {/* Place Name only */}
              <div className="flex-1 min-w-0 bg-darkCard px-5 py-3 rounded-3xl">
                {reverseGeocodeResult.place && (
                  <span className="block w-full font-poppins font-semibold text-lg whitespace-normal break-words leading-snug">
                    {reverseGeocodeResult.place.name}
                  </span>
                )}
                {!reverseGeocodeResult.place && reverseGeocodeResult.locality && (
                  <span className="block w-full font-poppins font-semibold text-lg whitespace-normal break-words leading-snug">
                    {reverseGeocodeResult.locality.name || 
                     reverseGeocodeResult.locality.context?.city_name || 
                     reverseGeocodeResult.locality.context?.state_name || 
                     reverseGeocodeResult.locality.context?.country_name || 
                     'Unknown Location'}
                  </span>
                )}
                {!reverseGeocodeResult.place && !reverseGeocodeResult.locality && (
                  <span className="italic text-white/60">Tap to select place</span>
                )}
              </div>
              {/* Circular buttons (stop propagation) */}
              <div className="flex items-center gap-3 self-center" onClick={e => e.stopPropagation()}>
                <button
                  onClick={handleSaveToTrippin}
                  disabled={reverseGeocodeLoading || savingToTrippin || flashing || savedToTrippin}
                  className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-colors ${savedToTrippin ? 'bg-emerald-500' : 'bg-mainLightColor'} disabled:opacity-50`}
                  aria-label={savedToTrippin ? 'Saved to Trippin' : 'Save to Trippin'}
                >
                  {savingToTrippin ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : savedToTrippin ? (
                    <span className="text-white text-xl">✓</span>
                  ) : (
                    <img src="assets/icons/trippin.png" alt="Trippin" className="w-full h-full object-cover rounded-full" />
                  )}
                </button>
                <button
                  onClick={handleFlashIt}
                  disabled={reverseGeocodeLoading || savingToTrippin || flashing || flashed}
                  className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-colors ${flashed ? 'bg-yellow-400' : 'bg-mainLightColor'} disabled:opacity-50`}
                  aria-label={flashed ? 'Flashed' : 'Flash it'}
                >
                  {flashing ? (
                    <CircularProgress size={20} sx={{ color: flashed ? 'black' : 'white' }} />
                  ) : flashed ? (
                    <span className="text-black text-xl">✓</span>
                  ) : (
                    <img src="assets/icons/flash_on.png" alt="Flash" className="w-7 h-7 object-contain bg-mainLightColor" />
                  )}
                </button>
              </div>
            </div>
          ))}

          <Modal isOpen={isModalOpen} onRequestClose={cancelStartTrippin}>
            <h2>Start a new Trippin?</h2>
            <p>You don't have an active <b>trippin</b>.<br />Would you like to start a new one?</p>
            <div>
              <button onClick={confirmStartTrippin}>Yes</button>
              <button onClick={cancelStartTrippin}>Cancel</button>
            </div>
          </Modal>

          <Dialog
            open={isTagDialogOpen}
            onClose={() => setIsTagDialogOpen(false)}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: "#181818",
                  color: "white",
                  borderRadius: "12px",
                  padding: "14px",
                  maxWidth: "400px",
                  width: "100%",
                },
              },
            }}
          >
            <DialogTitle>Tag users</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
                sx={{
                  backgroundColor: '#1f1f1f',
                  borderRadius: '10px',
                  color: '#f5f5f5',
                  '& .MuiInputBase-root': {
                    color: '#f5f5f5',
                    fontSize: '14px',
                    padding: '4px 10px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 4px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#d1d5db',
                    opacity: 1,
                  },
                }}
              />
              <List sx={{ maxHeight: "250px", overflowY: "auto", mt: 1 }}>
                {displayedUsers.map((user) => (
                  <ListItem
                    key={user.user_id}
                    button
                    onClick={() => toggleUserSelection(user.user_id)}
                    sx={{
                      padding: 0,
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "none" },
                    }}
                  >
                    <Checkbox 
                      checked={selectedUsers.includes(user.user_id)}
                      sx={{
                        color: '#555',
                        '&.Mui-checked': {
                          color: '#48c9b0',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 24,
                        }
                      }}
                    />
                    <ListItemText primary={user.username} />
                  </ListItem>
                ))}

                {loading && (
                  <ListItem sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  </ListItem>
                )}
              </List>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-evenly" }}>
              <Button
                onClick={() => confirmTags(false)}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  padding: "5px 16px",
                  backgroundColor: "#48c9b0",
                  color: "#181818",
                  borderRadius: "5px",
                }}
              >
                Flash
              </Button>
              <Button
                onClick={() => confirmTags(true)}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  padding: "5px 16px",
                  backgroundColor: "#48c9b0",
                  color: "#181818",
                  borderRadius: "5px",
                }}
              >
                Flash close
              </Button>
              <Button
                onClick={() => setIsTagDialogOpen(false)}
                sx={{ textTransform: "none", fontSize: "16px", color: "white" }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <>
          <button className="close-button" onClick={handleDiscard}>
            <img src="assets/icons/close.png" alt="Close" />
          </button>
          <p style={{ color: "white", fontSize: "large" }}>No photo or video to display</p>
        </>
      )}

      {/* Locality confirmation dialog */}
      <Dialog 
        open={placeDialogOpen} 
        onClose={closePlaceDialog} 
        fullWidth 
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#181818',
              color: 'white',
              borderRadius: '12px',
              padding: '14px',
              maxWidth: '600px',
              width: '100%',
            }
          }
        }}
      >
        <DialogTitle>
          {placeOptionsData ? 'Select Place' : 'Confirm Place'}
        </DialogTitle>
        <DialogContent>
          {placeOptionsData ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, color: '#bbb' }}>
                {placeOptionsData.type === 'initial_selection' ? 
                  'Multiple places found at this location. Please select the most appropriate one:' :
                  'Multiple places found at this location. Please select the correct one:'
                }
              </div>
              <List dense>
                {placeOptionsData.options?.map((option) => (
                  <ListItem
                    key={option.id}
                    style={{ 
                      border: selectedSuggestionId === option.id ? '2px solid #48c9b0' : '1px solid #333',
                      borderRadius: 8,
                      marginBottom: 8,
                      padding: 8,
                      background: '#121212'
                    }}
                  >
                    <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <div 
                        style={{ flex: 1, cursor: 'pointer' }}
                        onClick={() => setSelectedSuggestionId(option.id)}
                      >
                        <ListItemText 
                          primary={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600, color: '#fff' }}>{option.name}</span>
                              {option.source_type && option.source_type !== 'pending_place' && (
                                <span style={{
                                  fontSize: 10,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  background: '#0f2d25',
                                  color: '#6ee7b7',
                                  border: '1px solid #065f46'
                                }}>
                                  {'VERIFIED'}
                                </span>
                              )}
                            </div>
                          }
                          secondary={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {option.category && <div style={{ fontSize: 13, color: '#ddd' }}>Category: {option.category}</div>}
                              {option.distance && <div style={{ fontSize: 12, color: '#999' }}>~ {Math.round(option.distance)}m away</div>}
                              {option.locality && (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                                  {option.locality.area && (
                                    <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>Area: {option.locality.area}</span>
                                  )}
                                  {option.locality.village && (
                                    <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>Village: {option.locality.village}</span>
                                  )}
                                  {option.locality.city && (
                                    <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>City: {option.locality.city}</span>
                                  )}
                                  {option.locality.state && (
                                    <span style={{ fontSize: 11, background: '#232323', color: '#e5e7eb', padding: '2px 6px', borderRadius: 999 }}>State: {option.locality.state}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </ListItem>
                ))}
              </List>
              <Button
                variant="outlined"
                onClick={() => {
                  console.log("Opening edit dialog from change dialog, available localitySearchResults:", placeOptionsData?.localitySearchResults);
                  setPlaceNameInput('');
                  setSelectedCategory('');

                  populateCreatePlaceForm();
                  setEditPlaceDialogOpen(true);
                }}
                sx={{ alignSelf: 'flex-start', marginTop: 1, borderColor: '#48c9b0', color: '#e5e7eb', '&:hover': { borderColor: '#3fb6a0' } }}
              >
                + Create New Place
              </Button>
            </div>
          ) : (
            // Original place confirmation view
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <TextField 
                label="Place name (optional)" 
                value={placeNameInput} 
                onChange={(e) => setPlaceNameInput(e.target.value)} 
                fullWidth 
                sx={darkTextFieldSx}
              />
              {!!nearbySuggestions.length && (
                <>
                  <div style={{ fontSize: 12, color: '#cbd5e1' }}>Nearby suggestions</div>
                  <List dense>
                    {nearbySuggestions.filter((it) => /^(b|p|c):/.test(String(it.id))).map((it) => {
                      // Compose locality string for display
                      const loc = it.locality || {};
                      const localityStr = [loc.village, loc.city, loc.state, loc.country].filter(Boolean).join(', ');
                      return (
                        <ListItem
                          key={it.id}
                          button
                          selected={selectedSuggestionId === it.id}
                          onClick={() => {
                            setSelectedSuggestionId(it.id);
                            setPlaceNameInput(it.label || '');
                            // Prefill locality fields if available
                            setEditedLocality({
                              city: loc.city || '',
                              state: loc.state || '',
                              country: loc.country || '',
                              village: loc.village || ''
                            });
                          }}
                          sx={{
                            border: '1px solid #333',
                            borderRadius: '8px',
                            mb: 1,
                            backgroundColor: selectedSuggestionId === it.id ? '#0b2532' : '#121212',
                            '& .MuiListItemText-primary': { color: '#fff' },
                            '& .MuiListItemText-secondary': { color: '#cbd5e1' }
                          }}
                        >
                          <ListItemText
                            primary={
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{it.label}</span>
                                {it.source_type && (
                                  <span style={{
                                    fontSize: 10,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    background: it.source_type === 'pending_place' ? '#2b1d12' : '#0f2d25',
                                    color: it.source_type === 'pending_place' ? '#f8b26a' : '#6ee7b7',
                                    border: '1px solid ' + (it.source_type === 'pending_place' ? '#b45309' : '#065f46')
                                  }}>
                                    {it.source_type === 'pending_place' ? 'PENDING' : 'VERIFIED'}
                                  </span>
                                )}
                              </div>
                            }
                            secondary={localityStr}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlaceDialog} disabled={placeDialogBusy} sx={{ color: '#fff' }}>Cancel</Button>
          {placeOptionsData ? (
            <Button 
              onClick={() => handlePlaceOptionSelection(selectedSuggestionId)} 
              disabled={placeDialogBusy || !selectedSuggestionId} 
              variant="contained"
              sx={{ backgroundColor: '#48c9b0', color: '#181818', '&:hover': { backgroundColor: '#3fb6a0' } }}
            >
              Use Selected Place
            </Button>
          ) : (
            <Button 
              onClick={submitPlaceDialog} 
              disabled={placeDialogBusy || (!selectedSuggestionId && !placeNameInput?.trim())} 
              variant="contained"
              sx={{ backgroundColor: '#48c9b0', color: '#181818', '&:hover': { backgroundColor: '#3fb6a0' } }}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Place creation dialog */}
      <Dialog 
        open={editPlaceDialogOpen} 
        onClose={closeEditPlaceDialog} 
        fullWidth 
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#181818',
              color: 'white',
              borderRadius: '12px',
              padding: '14px',
              maxWidth: '600px',
              width: '100%'
            }
          }
        }}
      >
        <DialogTitle>
          Create New Place
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
            <TextField
              label="Place Name (Optional)"
              value={placeNameInput}
              onChange={(e) => setPlaceNameInput(e.target.value)}
              fullWidth
              helperText="Leave empty if you only want to update locality information"
              sx={darkTextFieldSx}
            />
            
            <div style={{ position: 'relative' }}>
              <TextField
                label="Category"
                value={selectedCategory}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCategory(val);
                  if (!val.trim()) {
                    setIsCategoryValid(null);
                    setShowNoMatches(false);
                    setCategorySuggestions([]);
                  } else {
                    searchCategories(val);
                  }
                }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    await exactCheckCategory(selectedCategory);
                  }
                }}
                onBlur={() => {
                  // Delay hiding to allow click selection to register
                  blurTimeoutRef.current = setTimeout(() => {
                    setCategorySuggestions([]);
                    setShowNoMatches(false);
                  }, 150);
                }}
                onFocus={() => {
                  if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
                  if ((selectedCategory || '').trim()) searchCategories(selectedCategory);
                }}
                fullWidth
                placeholder="e.g. restaurant, cafe, shop, hotel..."
                error={showCategoryInlineError}
                inputProps={{ 'aria-autocomplete': 'list', autoComplete: 'off' }}
                sx={darkTextFieldSx}
              />

              {/* Suggestions dropdown (appears when user types) */}
              {selectedCategory && (
                <div
                  aria-label="Category suggestions"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '100%',
                    zIndex: 1200,
                    background: '#1f1f1f',
                    border: '1px solid #333',
                    borderRadius: 4,
                    maxHeight: 220,
                    overflow: 'auto',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.3)'
                  }}
                >
                  {((categorySuggestions || []).length === 0 && showNoMatches) ? (
                    <div style={{ padding: 8, color: '#bbb' }}>No matches</div>
                  ) : (
                    (categorySuggestions || []).map((opt) => (
                      <div
                        key={opt.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setSelectedCategory(opt.name);
                          setCategorySuggestions([]);
                          setShowNoMatches(false);
                          setIsCategoryValid(true);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedCategory(opt.name); setCategorySuggestions([]); setShowNoMatches(false); setIsCategoryValid(true); } }}
                        style={{ padding: 8, borderBottom: '1px solid #333', cursor: 'pointer', color: '#e5e7eb' }}
                      >
                        {opt.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 8 }}>
              Locality Information
            </div>
            
            {(reverseGeocodeResult?.locality || reverseGeocodeResult?.fromCache) && (
              <div style={{ 
                fontSize: 14, 
                color: '#6ee7b7', 
                backgroundColor: '#0f2d25',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #065f46'
              }}>
                Locality information prefilled from {reverseGeocodeResult?.fromCache ? 'cached' : 'recent'} location lookup
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <TextField
                label="Area"
                value={editedLocality.area || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, area: e.target.value }))}
                placeholder="Enter area name"
                size="small"
                sx={darkTextFieldSx}
              />
              <TextField
                label="Village"
                value={editedLocality.village || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, village: e.target.value }))}
                placeholder="Enter village name"
                size="small"
                sx={darkTextFieldSx}
              />
              <TextField
                label="City"
                value={editedLocality.city || ''}
                onChange={(e) => setEditedLocality(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Enter city name"
                size="small"
                sx={darkTextFieldSx}
              />
            </div>
            <TextField
              label="State/Province/District"
              value={editedLocality.state || ''}
              onChange={(e) => setEditedLocality(prev => ({ ...prev, state: e.target.value }))}
              placeholder="Enter state, province, or district"
              size="small"
              sx={darkTextFieldSx}
            />
            <TextField
              label="Country"
              value={editedLocality.country || 'Cyprus'}
              disabled
              placeholder="Country"
              size="small"
              helperText="Country cannot be changed and defaults to Cyprus."
              sx={darkTextFieldSx}
            />
            
            {/* <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 8 }}>
              Type in any field to search existing localities or create new ones if not found.
            </div>
            
            <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 8 }}>
              Only village, city, and state can be edited. Country is locked.
            </div> */}
            
            <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 8 }}>
              Fill in any missing locality information to help build our community database.
              This information helps other users find places in your area.
            </div>
            
            {/* Validation feedback */}
            {isInputUnchangedFromCache() && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>
                Please modify at least one field or select an existing place suggestion.
              </div>
            )}
            
            {/* If the input matches an existing suggestion we still allow creation.
                Backend will dedupe or return the existing place. */}
            
            {(((placeNameInput || '').trim() && !(selectedCategory || '').trim()) || (!(placeNameInput || '').trim() && (selectedCategory || '').trim())) && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: 8 }}>
                Both place name and category are required when creating a new place.
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditPlaceDialog} disabled={placeDialogBusy} sx={{ color: '#fff' }}>
            Cancel
          </Button>
          <Button 
            onClick={createNewPlace}
            disabled={
              placeDialogBusy || 
              isInputUnchangedFromCache() || 
              (((placeNameInput || '').trim() && !(selectedCategory || '').trim()) || (!(placeNameInput || '').trim() && (selectedCategory || '').trim()))
              || showCategoryInlineError
            }
            variant="contained"
            sx={{ backgroundColor: '#48c9b0', color: '#181818', '&:hover': { backgroundColor: '#3fb6a0' } }}
          >
            {(placeNameInput || '').trim() ? 'Create Place' : 'Update Locality'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
